import { QWIKBOX_STORAGE_BASE_URL } from 'src/app/constants/storage.constants';
import * as _ from 'lodash';
import { Team, TeamGroup } from './team';
import { Game } from './game';
import { User } from './user';
import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'
import { RouteLine } from '../components/playbook/playbook-stage/playbook-stage.component';
import { Playlist } from './playlist';



export enum PlayerGraphicType {
  circle = 'circle',
  oval = 'oval',
  triangle = 'triangle',
  text = 'text',
  rectangle = 'rectangle',
  hexagon = 'hexagon',
  diamond = 'diamond'
}
export enum PlayerGraphicAlignment {
  text = 'text',
  none = 'none',
  left = 'left',
  right = 'right',
  middle = 'middle',
  fill = 'fill'
}
export interface PlayerGraphicData {type: PlayerGraphicType, color?: string, alignment?: PlayerGraphicAlignment};

export enum RouteLineType {
  normal = 'normal',
  dotted = 'dotted',
  zig = 'zig'
}
export enum RouteEndingType {
  arrow = 'arrow',
  block = 'block',
  none = 'none'
}
export interface RouteData {lineType: RouteLineType, endingType: RouteEndingType, color?: string, curved?: boolean};

export interface TextData {bold?: boolean, italic?: boolean, color?: string, fontSize?: number};

export enum CoverageType {
  circle = 'circle',
  rectangle = 'rectangle'
}
export interface CoverageData {type: CoverageType, shadeColor?: string};


export enum PlaybookFieldSize {
  short = 'Short Field',
  long = 'Long Field'
}
export enum PlaybookFieldLocation {
  midField = 'Mid-Field',
  backedUp = 'Backed Up',
  redZone = 'Red Zone'
}
export enum PlaybookHashConfiguration {
  default = 'Default',
  compact = 'Compact'
}
export interface PlaybookFieldOptions {fieldSize: PlaybookFieldSize, fieldLocation: PlaybookFieldLocation, hashConfiguration: PlaybookHashConfiguration, showGridlines?: boolean, showCrossSection: boolean};
export const defaultFieldOptions: PlaybookFieldOptions = {
  fieldSize: PlaybookFieldSize.short,
  fieldLocation: PlaybookFieldLocation.midField,
  hashConfiguration: PlaybookHashConfiguration.default,
  showGridlines: false,
  showCrossSection: true
};

const footballFieldWidthFeet = 160;
const defaultHashOuterEdgeRatio = 0.28;
const defaultYardLineNumberOffsetRatio = defaultHashOuterEdgeRatio/2;

export const normalizePlaybookFieldOptions = (fieldOptions?: Partial<PlaybookFieldOptions>): PlaybookFieldOptions => ({
  fieldSize: fieldOptions?.fieldSize ?? defaultFieldOptions.fieldSize,
  fieldLocation: fieldOptions?.fieldLocation ?? defaultFieldOptions.fieldLocation,
  hashConfiguration: fieldOptions?.hashConfiguration ?? defaultFieldOptions.hashConfiguration,
  showGridlines: fieldOptions?.showGridlines ?? defaultFieldOptions.showGridlines,
  showCrossSection: fieldOptions?.showCrossSection ?? defaultFieldOptions.showCrossSection
});

export const getPlaybookInteriorHashStartPositions = (
  fieldWidth: number,
  hashWidth: number,
  hashConfiguration: PlaybookHashConfiguration = PlaybookHashConfiguration.default
): [number, number] => {
  if (hashConfiguration === PlaybookHashConfiguration.compact) {
    const inboundEdge = fieldWidth * (60/footballFieldWidthFeet);
    return [inboundEdge-hashWidth, fieldWidth-inboundEdge];
  }

  const outerEdge = fieldWidth * defaultHashOuterEdgeRatio;
  return [outerEdge, fieldWidth-outerEdge-hashWidth];
};

export const getPlaybookHashWidth = (fieldWidth: number, fieldSize: PlaybookFieldSize): number => {
  return fieldWidth/(fieldSize === PlaybookFieldSize.long ? 80 : 60);
};

export const constrainPlaybookCenterXToHashes = (
  centerX: number,
  fieldWidth: number,
  hashWidth: number,
  hashConfiguration: PlaybookHashConfiguration = PlaybookHashConfiguration.default,
  playerHalfWidth: number = 0
): number => {
  const [leftHashStart, rightHashStart] = getPlaybookInteriorHashStartPositions(fieldWidth, hashWidth, hashConfiguration);
  const leftInnerEdge = leftHashStart+hashWidth+playerHalfWidth;
  const rightInnerEdge = rightHashStart-playerHalfWidth;
  return Math.max(leftInnerEdge, Math.min(centerX, rightInnerEdge));
};

export const getPlaybookYardLineNumberOffset = (fieldWidth: number): number => fieldWidth * defaultYardLineNumberOffsetRatio;


export enum PlaybookSort {
  formation = 'Formation',
  oppForm = 'Opp. Form',
  playName = 'Play Name',
  personnel = 'Personnel',
  offPlay = 'Off. Play',
  opponent = 'Opponent',
  blitz = 'Stunt/Blitz',
  coverage = 'Coverage'
}
export const playbookSortOptions: PlaybookSort[] = [PlaybookSort.formation, PlaybookSort.oppForm, PlaybookSort.playName, PlaybookSort.personnel, PlaybookSort.offPlay, PlaybookSort.opponent];

export class PlaybookPlayEditController {
  addingPlayerData?: PlayerGraphicData;
  addingRouteData?: RouteData;
  // addingTextData?: TextData;
  // addingCoverageData?: CoverageData;
  fieldOptions: PlaybookFieldOptions = _.cloneDeep(defaultFieldOptions);
  freeDrawEnabled: boolean = false;
  onPlayerAlignmentSelected?(alignment: PlayerGraphicAlignment): void;
  onPlayerColorSelected?(color: string): void;
  onPlayerSelected?(player: PlaybookGraphicData): void;
  selectPlayerTool?(): void;
  onRouteConfigChanged?(lineType: RouteLineType, endingType: RouteEndingType, color: string, curved: boolean): void;
  onRoutePresetSelected?(preset: RoutePreset): RoutePreset;
  onRouteSelected?(route: PlaybookRoute): void;
  selectRouteTool?(): void;
  onAddTextBox?(data: TextData): void;
  onTextFontStyleSelected?(bold: boolean, italic: boolean): void;
  onTextColorSelected?(color: string): void;
  onTextFontSizeChanged?(fontSize: number): void;
  onTextBoxSelected?(textBox: PlaybookTextbox): void;
  onAddCoverage?(data: CoverageData): void;
  onCoverageColorSelected?(shadeColor: string): void;
  onCoverageSelected?(coverage: PlaybookCoverage): void;
  onItemsSelectedChange?(drawing: PlaybookGraphicData[], coverages: PlaybookCoverage[], routes: PlaybookRoute[], textboxes: PlaybookTextbox[]): void;
  applyTemplate?(template: PlaybookTemplate): void;
  onFieldOptionsChanged?(): void;
  onFlipPlayVertical?(): void;
  onFlipPlayHorizontal?(type: 'all' | 'offense' | 'defense'): void;
  onPlayFormationAdded?(formationPlay: PlaybookPlay): void;
  onDelete?(): void;
  onUndo?(): void;
  onRedo?(): void;
  onZoomChanged?(): void;
  getSnapshot?(): string;
  onStageDestroyedSnapshot?(dataUrl: string): void;
}

export const playbookColors: string[] = ['#000000', '#000FFF', '#0FFDD8', '#00FE1B', '#FEF200', '#ff7400', '#FF4433', '#FF0000', '#FF00DD', '#B200FF'];

export interface PlaybookAppConfig {
  defaultRadius: number;
  appWidth: number;
  appHeight: number;
  losOffset: number;
  defaultStrokeWidth: number;
  selectedStrokeWidth: number;
  playerFontSize: number;
};

export const playbookAppConfigMap: Map<PlaybookFieldSize, PlaybookAppConfig> = new Map();
playbookAppConfigMap.set(PlaybookFieldSize.short, {
  defaultRadius: 12,
  appWidth: 1000,
  appHeight: 700,
  losOffset: 10,
  defaultStrokeWidth: 2,
  selectedStrokeWidth: 4,
  playerFontSize: 12
});
playbookAppConfigMap.set(PlaybookFieldSize.long, {
  defaultRadius: 8,
  appWidth: 1000,
  appHeight: 700,
  losOffset: 5,
  defaultStrokeWidth: 2,
  selectedStrokeWidth: 4,
  playerFontSize: 8
});

export class PlaybookPlay {
  _id?: string;
  teamid: Team['_id'];
  name: string = '';
  type: PlaybookPlayType;
  fieldOptions?: PlaybookFieldOptions; //new
  flipped?: boolean;
  oppPlay: string = '';
  formation: string = '';
  oppFormation: string = '';
  blitz: string = '';
  coverage: string = '';
  opponent: string = '';
  personnel: PlaybookPersonnel;
  scriptOnly: boolean = false;
  notes: Array<any> = [];
  assignments: Array<PlaybookAssignment> = [];
  drawing: PlaybookGraphicData[];
  coverages: PlaybookCoverage[];
  routes: PlaybookRoute[];
  textboxes: PlaybookTextbox[];
  losY?: number; //new
  clips: PlaybookClip[] = [];
  url: string;
  deleted?: boolean; // new
  deletionDate?: Date; //new
  deletionUserid?: User['_id']; //new
  groupids: string[]; // sharing
  userids: User['_id'][]; // sharing
  exported?: boolean; // team sharing
  audioRecording?: boolean;
  audioRecordingDuration?: number;
  importSource?: PlaybookImportSource;
  createdAt?: Date;
  updatedAt?: Date;
  numBackfield?: number;

  //extra stuff for scripts
  mainText?: string;
  subText?: string;
  viewed?: boolean;
  hash?: string;
  offplay?: string;
  backfield?: string;
  defformation?: string;
  deffront?: string;
  defplay?: string;
  motion?: string;
  playdirection?: string;
  yardline?: string | number;
  down?: string | number;
  distance?: string | number;
  playtype?: string;
  occurrences?: number;
  percentage?: number;
  gnLsTotal?: number;
  gnLsAvg?: number;
  deletionUser?: User;
  audioRecordingBlob?: Blob;
  scriptid?: string;

  constructor() {
    for(let i=0; i<11; i++) {
      this.assignments.push(new PlaybookAssignment());
    }
  }
}

export interface PlaybookImportSource {
  provider: 'hudl';
  importid: string;
  documentHash: string;
  filename: string;
  sourcePage?: number;
  sourcePlayIndex?: number;
  sourceTitle?: string;
  importedBy: User['_id'];
  importedAt: Date;
}

export interface HudlPlaybookImportPlay {
  sourcePage: number;
  sourcePlayIndex?: number;
  sourceTitle?: string;
  name: string;
  formation: string;
  oppFormation?: string;
  personnel?: PlaybookPersonnel;
  fieldOptions: PlaybookFieldOptions;
  losY: number;
  drawing: PlaybookGraphicData[];
  coverages: PlaybookCoverage[];
  routes: PlaybookRoute[];
  textboxes: PlaybookTextbox[];
  assignments?: PlaybookAssignment[];
}

export interface HudlPlaybookImportRequest {
  provider: 'hudl';
  documentHash: string;
  filename: string;
  playType: PlaybookPlayType;
  install?: {
    installid?: PlaybookFolder['_id'];
    name?: string;
    date?: Date;
    seasonid?: number;
  };
  plays: HudlPlaybookImportPlay[];
}

export interface HudlPlaybookImportResult {
  importid: string;
  install?: PlaybookFolder;
  plays: PlaybookPlay[];
  duplicate: boolean;
  warnings: string[];
}

export interface PlaybookClip {
  gameid: Game['_id'];
  clipnumber: number;
  clip_link: string;
  thumbnail_link?: string;
}

export interface PlaybookGraphicData {
  playerGraphic: PlaybookPlayer;
  routes?: PlaybookRoute[];

  //not in db
  konvaGroup?: Konva.Group;
}

export interface PlaybookPlayer {
  type: PlayerGraphicType;
  tag?: string;
  color?: string;
  alignment?: PlayerGraphicAlignment; //new
  position: {
    x: number;
    y: number;
  };

  //not in db
  konvaShape?: Konva.Shape;
  konvaText?: Konva.Text;
}

export interface PlaybookRoute {
  points: {x: number, y: number}[];
  lineType?: RouteLineType; //new
  endingType?: RouteEndingType; //new
  color?: string;
  curved?: boolean;  //new
  subroute?: boolean;

  //not in db
  konvaLine?: RouteLine;
}

export interface PlaybookCoverage {
  type: CoverageType; // new (changed)
  position: {
    x: number;
    y: number;
  };
  height?: number;
  width?: number;
  shadeColor?: string;

  //not in db
  konvaShape?: Konva.Shape;
  konvaTransformer?: Konva.Transformer;
}

export interface PlaybookTextbox {
  x: number;
  y: number;
  width?: number; //new
  text?: string;
  color?: string; //new
  bold?: boolean; //new
  italic?: boolean;
  fontSize?: number; //new

  //not in db
  konvaText?: Konva.Text;
  konvaTransformer?: Konva.Transformer;
  textarea?: HTMLDivElement;
}

export class PlaybookAssignment {
  player: string = '';
  notes: string = '';
}

export interface PlaybookFolder {
  _id?: string;
  teamid: Team['_id'];
  name: string;
  type: PlaybookPlayType;
	date?: Date;
  seasonid?: number;
	grouporder?: {[groupid: string]: number};
  playids?: PlaybookPlay['_id'][];
	playorder?: {[playid: string]: number};
  groupids?: TeamGroup['_id'][]; // sharing
  userids?: User['_id'][]; // sharing
  externalSharingEnabled?: boolean;

  //not in db
  loading?: boolean;
  childFolders?: PlaybookFolder[];
  childGroups?: PlaybookFolderGroup[];
  plays?: PlaybookPlay[];
  nonGroupedPlays?: PlaybookPlay[];
  count?: number;
  depth?: number;
  installFolder?: boolean;
  viewData?: PlaybookInstallView;
}

export interface PlaybookFolderGroup {
  _id?: string;
  teamid: Team['_id'];
  name: string;
  type: PlaybookPlayType;
  parentfolderid: PlaybookFolder['_id'];
  playids?: PlaybookPlay['_id'][];
	playorder?: {[playid: string]: number};

  //not in db
  plays?: PlaybookPlay[];
  collapsed?: boolean;
  wasCollapsed?: boolean;
  highlight?: boolean;
  viewed?: boolean;
}

export interface PlaybookGroup {
  _id?: string;
  name: string;
  plays: PlaybookPlay[];
	date?: Date;
}

export interface PlaybookInstallView {
  _id?: string;
  userid: User['_id'];
  installid: PlaybookFolder['_id'];
  viewedPlayids?: string[];
  viewTime?: number;
  updatedAt?: Date;
  createdAt?: Date;
}

export interface PlaybookScriptView {
  _id?: string;
  userid: User['_id'];
  scriptid: PlaybookScript['_id'];
  viewedPlayids?: string[];
  viewTime?: number;
  updatedAt?: Date;
  createdAt?: Date;
}

export interface PlaybookViewTimeEvent {
  eventid: string;
  schemaVersion: 2;
  targetType: 'install' | 'script';
  targetid: string;
  durationMs: number;
  occurredAt: string;
  source: 'web' | 'flutter';
  sourceVersion: string;
}

export interface PlaybookViewTimeResult {
  acceptedEventids: string[];
  rejected: Array<{eventid?: string; reason: string}>;
}

export interface PlaybookViewOverview {
  user: User;
  viewedPlayids: string[];
  progress: number;
  viewTime: number;
  updatedAt?: Date;
}

export interface PlaybookInstallShare {
  _id?: string;
  sourceTeamid: Team['_id'];
  destTeamid?: Team['_id'];
  sourceInstallid: PlaybookFolder['_id'];
  destInstallid?: PlaybookFolder['_id'];
  name: string;
  type: PlaybookPlayType;
  emails?: string[];
  plays?: PlaybookPlay[];
  exported?: boolean;
  viewed?: boolean;
  createdAt?: Date;
  updatedAt?: Date;

  //not in db
  sourceTeam?: Team;
  destTeam?: Team;
}

export interface PlaybookScript {
  _id?: string;
  teamid: Team['_id'];
  name: string;
	gameid?: Game['_id'];
  playlistid?: Playlist['_id'];
  scoutGameids?: Game['_id'][];
  seasonid?: number;
  createdByUserid?: User['_id'];
  plays: PlaybookPlay[];
  columns: ScriptColumn[];
  qwikScript?: boolean;
  order?: number;
  groupids?: TeamGroup['_id'][]; // sharing
  userids?: User['_id'][]; // sharing

	//not in db
	game?: Game;
  playlist?: Playlist;
  createdByUser?: User;
  viewData?: PlaybookScriptView;
}

export interface ScriptColumn {
  display: string;
  value: string;
}

export interface PlaybookTemplateCategory {
  _id?: string;
  teamid: Team['_id'];
  name: string;

  //not in db
  collapsed?: boolean;
  templates?: PlaybookTemplate[];
}

export interface PlaybookTemplate {
  _id?: string;
  teamid: Team['_id'];
  categoryid: PlaybookTemplateCategory['_id'];
  name: string;
  losY: number;
  drawing?: PlaybookGraphicData[];
  coverages?: PlaybookCoverage[];
  routes?: PlaybookRoute[];
  textboxes?: PlaybookTextbox[];

  //not in db
  playPreview?: PlaybookPlay;
}

export const assignPlayPreviewToTemplate = (template: PlaybookTemplate) => {
  if (!template?.losY) return;
  template.playPreview = new PlaybookPlay();
  template.playPreview._id = template._id;
  template.playPreview.type = PlaybookPlayType.offense;
  template.playPreview.fieldOptions = _.cloneDeep(defaultFieldOptions);
  template.losY = template.losY;
  template.playPreview.mainText = template.playPreview.name = template.name;
  template.playPreview.drawing = _.clone(template.drawing ?? []);
  template.playPreview.coverages = _.clone(template.coverages ?? []);
  template.playPreview.routes = _.clone(template.routes ?? []);
  template.playPreview.textboxes = _.clone(template.textboxes ?? []);
}

export enum PlaybookType {
  offense = 'offense',
  defense = 'defense',
  specialteams = 'specialteams',
  scripts = 'scripts'
}
export enum PlaybookPlayType {
  offense = 'offense',
  defense = 'defense',
  specialteams = 'specialteams'
}
export enum ExtraFolderCategory {
  sharedWithMe = 'Shared with me',
  sentHistory = 'Sent History',
  deleted = 'Deleted'
};
export type PlaybookPersonnel = '00' | '01' | '02' | '10' | '11' | '12' | '13' | '20' | '21' | '22' | '23';

export const personnelOptions: PlaybookPersonnel[] = ['00', '01', '02', '10', '11', '12', '13', '20', '21', '22', '23'];

export interface PlaybookStructureState {
  expandedNodeIds: string[];
  lastScrollPos: number;
}

export class PlayGrouping {
  name: string;
  plays: PlaybookPlay[] = [];
  collapsed: boolean = true;
  cannotDelete: boolean

  constructor(name: string, plays: PlaybookPlay[], cannotDelete: boolean = false) {
    this.name = name;
    this.plays = _.orderBy(plays ?? [], play => _.toUpper(play.mainText), 'asc');
    this.cannotDelete = cannotDelete;
  }

  getPreviewPlay(): PlaybookPlay {
    return _.first(this.plays);
  }
}

export const getPlaybookPlayDisplay = (play: PlaybookPlay, script?: PlaybookScript): {mainText: string, subText: string} => {
  let mainText = '';
  let subText = '';
  if (play) {
    if (script?.columns?.length) {
      mainText = null;
      subText = '';
      _.forEach(script.columns, column => {
        if (play[column.value]) {
          if (subText.length > 0) subText += ' ';
          if (column.value === 'hash') subText += `(${play[column.value]})`;
          else subText += play[column.value];
        }
      });
    }
    else if (play.type === PlaybookPlayType.offense || play.type === PlaybookPlayType.specialteams) {
      mainText = play.formation;
      subText = `${play.personnel ? `(${play.personnel}) `: ''}${play.name} ${play.oppFormation ? ' vs. ' + play.oppFormation : ''}`;
    }
    else if (play.type === PlaybookPlayType.defense) {
      mainText = `${play.formation} ${play.blitz ? play.blitz : ''} ${play.coverage ? play.coverage : ''}`;
      subText = play.oppFormation || play.oppPlay ? ' vs. ' + `${play.oppFormation || ''} ${play.oppPlay || ''}` : '';
    }
  }
  return {mainText, subText};
}

export const scriptColumnOptions: ScriptColumn[] = [
  {
    display: 'HASH',
    value: 'hash'
  }, {
    display: 'PERSONNEL',
    value: 'personnel'
  }, {
    display: 'OFF FORM',
    value: 'formation'
  }, {
    display: 'OFF PLAY',
    value: 'offplay'
  }, {
    display: 'BACKFIELD',
    value: 'backfield'
  }, {
    display: 'PLAY TYPE',
    value: 'playtype'
  }, {
    display: 'DEF FORM',
    value: 'defformation'
  }, {
    display: 'DEF FRONT',
    value: 'deffront'
  }, {
    display: 'DEF PLAY',
    value: 'defplay'
  }, {
    display: 'COVERAGE',
    value: 'coverage'
  }, {
    display: 'BLITZ',
    value: 'blitz'
  }, {
    display: 'MOTION',
    value: 'motion'
  }, {
    display: 'PLAY DIR',
    value: 'playdirection'
  }, {
    display: 'YARD LN',
    value: 'yardline'
  }, {
    display: 'DOWN',
    value: 'down'
  }, {
    display: 'DISTANCE',
    value: 'distance'
  }
];

export const defaultScriptColumns: ScriptColumn[] = [
  {
    display: 'HASH',
    value: 'hash'
  }, {
    display: 'OFF FORM',
    value: 'formation'
  }, {
    display: 'OFF PLAY',
    value: 'offplay'
  }, {
    display: 'DEF FRONT',
    value: 'deffront'
  }
];

export interface RoutePreset {
  name: string;
  points: {x: number, y: number}[];
}

export const routePresets: RoutePreset[] = [
  {
    name: 'FLAT',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -90
    }, {
      x: -125,
      y: -90
    }]
  }, {
    name: 'SLANT',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -90
    }, {
      x: 125,
      y: -135
    }]
  }, {
    name: 'COMEBACK',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -270
    }, {
      x: -36,
      y: -234
    }]
  }, {
    name: 'CURL',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -270
    }, {
      x: 36,
      y: -234
    }]
  }, {
    name: 'OUT',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -270
    }, {
      x: -125,
      y: -270
    }]
  }, {
    name: 'IN',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -270
    }, {
      x: 125,
      y: -270
    }]
  }, {
    name: 'CORNER',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -270
    }, {
      x: -125,
      y: -360
    }]
  }, {
    name: 'POST',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -270
    }, {
      x: 125,
      y: -360
    }]
  }, {
    name: 'GO',
    points: [{
      x: 0,
      y: 0
    }, {
      x: 0,
      y: -360
    }]
  }, 
]

export const getMultiplePlaysWithoutKonva = (plays: PlaybookPlay[], deleteId: boolean = false) => {
  const playsWithoutKonva: PlaybookPlay[] = [];
  _.forEach(plays ?? [], play => {
    playsWithoutKonva.push(getPlayWithoutKonva(play, deleteId));
  });
  return playsWithoutKonva;
}

export const getPlayWithoutKonva = (play: PlaybookPlay, deleteId: boolean = false, clonePlay: boolean = true) => {
  const playToSave = clonePlay ? _.cloneDeep(play) : play;
  if (deleteId) delete playToSave._id;
  _.forEach(playToSave.drawing ?? [], drawing => {
    delete drawing.konvaGroup;
    if (drawing.playerGraphic) {
      delete drawing.playerGraphic.konvaShape;
      delete drawing.playerGraphic.konvaText;
    }
    _.forEach(drawing.routes ?? [], route => {
      delete route.konvaLine;
    });
  });
  _.forEach(playToSave.routes ?? [], route => {
    delete route.konvaLine;
  });
  _.forEach(playToSave.coverages ?? [], coverage => {
    delete coverage.konvaShape;
    delete coverage.konvaTransformer;
  });
  _.forEach(playToSave.textboxes ?? [], textbox => {
    delete textbox.konvaText;
    delete textbox.konvaTransformer;
    delete textbox.textarea;
  });
  delete playToSave.audioRecordingBlob;
  return playToSave;
}

export const getDefaultLosY = (size: PlaybookFieldSize, type: PlaybookPlayType) => {
  const formAppConfg = playbookAppConfigMap.get(size);
  const yardHeight = size === PlaybookFieldSize.long ? formAppConfg.appHeight/70 : formAppConfg.appHeight/28;
  let losY: number = 0;
  if (size === PlaybookFieldSize.short) {
    if (type === PlaybookPlayType.defense) losY = (yardHeight * 15);
    else losY = (yardHeight * 20);
  }
  else {
    if (type === PlaybookPlayType.defense) losY = (yardHeight * 13);
    else losY = (yardHeight * 57);
  }

  return losY;
}

export const getBaseFormationFromPlay = (play: PlaybookPlay): PlaybookPlay => {
  const formationPlay = getPlayWithoutKonva(play, true);
  if (!formationPlay.losY) {
    const size = formationPlay.fieldOptions?.fieldSize ?? PlaybookFieldSize.short;
    formationPlay.losY = getDefaultLosY(size, formationPlay.type);
  }

  const playersToRemove: PlaybookGraphicData[] = [];
  if (formationPlay.type === PlaybookPlayType.defense) { //removing offensive players
    _.forEach(formationPlay.drawing, player => {
      player.routes = [];
      if (player?.playerGraphic?.position?.y != null && ((!formationPlay.flipped && player.playerGraphic.position.y > formationPlay.losY) || (formationPlay.flipped && player.playerGraphic.position.y < formationPlay.losY))) {
        playersToRemove.push(player);
      }
    });
  }
  else { // removing defensive players
    _.forEach(formationPlay.drawing, player => {
      player.routes = [];
      if (player?.playerGraphic?.position?.y != null && ((formationPlay.flipped && player.playerGraphic.position.y > formationPlay.losY) || (!formationPlay.flipped && player.playerGraphic.position.y < formationPlay.losY))) {
        playersToRemove.push(player);
      }
    });
  }
  _.remove(formationPlay.drawing, player => _.includes(playersToRemove, player));
  
  formationPlay.routes = [];
  formationPlay.coverages = [];
  formationPlay.textboxes = [];
  formationPlay._id = uuidv4();
  return formationPlay;
}


export const getScriptPlayTitle = (play: PlaybookPlay, script: PlaybookScript) => {
  if (!script?.columns?.length || !play) return '';
  let text = '';
  _.forEach(script.columns, column => {
    if (play[column.value]) {
      if (text.length > 0) text += ' ';
      text += _.toString(play[column.value]);
    }
  });
  return text;
}

export const getImageUrlFromPlay = (play: PlaybookPlay, scriptid?: string) => {
  if (!play || !play._id) return null;
  const lastmod = (new Date().getTime() / 10).toFixed(0);
  return `${QWIKBOX_STORAGE_BASE_URL}/playbook${scriptid ? `/${scriptid}` : ''}/${play._id}.png?lastmod=${lastmod}`;
}

export const getPlaybookRecordingUrl = (playid: string, scriptid?: string) => {
  const lastmod = (new Date().getTime() / 10).toFixed(0);
	return `${QWIKBOX_STORAGE_BASE_URL}/playbook/recordings${scriptid ? `/${scriptid}` : ''}/${playid}.mp3?lastmod=${lastmod}`;
}

export const getBase64Image = (imgUrl: string) => {
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    let onFallback = false;

    // onload fires when the image is fully loaded, and has width and height
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL("image/jpeg");
      resolve(dataURL); // the base64 string
      canvas.remove();
    };
  
    img.onerror = () => {
      if (onFallback) resolve(null);
      else {
        onFallback = true;
        img.src = `${QWIKBOX_STORAGE_BASE_URL}/playbook/default.png?lastmod=${(new Date().getTime() / 10).toFixed(0)}`;
      }
    }
  
    if (imgUrl) img.src = imgUrl;
    else {
      onFallback = true;
      img.src = `${QWIKBOX_STORAGE_BASE_URL}/playbook/default.png?lastmod=${(new Date().getTime() / 10).toFixed(0)}`;
    }
  });
}

export interface FormationData {
  backfield?: string;
  formation?: string;
  players: PlaybookPlayer[],
  url?: string;
  numBackfield: number;
  sniffer?: boolean;

  //used to display data
  play?: PlaybookPlay;
}

export const getPlayFromFormationData = (formationData: FormationData, size: PlaybookFieldSize = PlaybookFieldSize.short): PlaybookPlay => {
  const play = new PlaybookPlay();
  play.type = PlaybookPlayType.offense;
  play._id = uuidv4();
  const appConfig = playbookAppConfigMap.get(size);
  const yardHeight = size === PlaybookFieldSize.long ? appConfig.appHeight/70 : appConfig.appHeight/28;
  play.losY = (yardHeight * (size === PlaybookFieldSize.long ? 40 : 15));
  play.fieldOptions = _.cloneDeep(defaultFieldOptions);
  play.fieldOptions.fieldSize = size;
  play.drawing = [];
  _.forEach(formationData.players ?? [], playerGraphic => {
    play.drawing.push({
      playerGraphic: _.cloneDeep(playerGraphic),
      routes: []
    });
  });
  return play;
}


const getFormationPlayer = (x: number, y: number, config?: {type?: PlayerGraphicType, alignment?: PlayerGraphicAlignment, color?: string, tag?: string}): PlaybookPlayer => {
  return {
    type: config?.type ?? PlayerGraphicType.circle,
    tag: config?.tag ?? "",
    color: config?.color ?? '#000000',
    alignment: config?.alignment ?? PlayerGraphicAlignment.none,
    position: {
      x,
      y
    }
  };
}

export const getFormationBackfieldAndSides = (size: PlaybookFieldSize = PlaybookFieldSize.short): {losPlayers: PlaybookPlayer[], formationBackfields: FormationData[], formationSides: FormationData[]} => {
  const formAppConfg = playbookAppConfigMap.get(size);
  const yardHeight = size === PlaybookFieldSize.long ? formAppConfg.appHeight/70 : formAppConfg.appHeight/28;
  const verticalYardUnit = size === PlaybookFieldSize.long ? (yardHeight*1.5) : yardHeight;
  const losY = (yardHeight * (size === PlaybookFieldSize.long ? 40 : 15));
  const centerX = formAppConfg.appWidth/2;
  const shapeWidth = formAppConfg.defaultRadius*2;
  
  const losPlayerY = (losY + formAppConfg.defaultRadius + formAppConfg.losOffset);
  const losPlayerSpacing = size === PlaybookFieldSize.long ? 6 : 8;
  
  const snifferLeftX = centerX - ((shapeWidth*1.5) + (losPlayerSpacing*2));
  const snifferRightX = centerX + ((shapeWidth*1.5) + (losPlayerSpacing*2));
  const snifferY = losPlayerY + shapeWidth + (losPlayerSpacing*2);
  
  const backY = losPlayerY + (shapeWidth*2);
  const wideX = (shapeWidth*5);
  const slotX = (shapeWidth*7.5);
  const tightSlotX = (shapeWidth*9.5);
  const tightX = (centerX - ((shapeWidth + losPlayerSpacing) * 3));
  const doubleTightX = (tightX - (shapeWidth + losPlayerSpacing));
  
  const losPlayers: PlaybookPlayer[] = [
    getFormationPlayer(centerX, losPlayerY, {type: PlayerGraphicType.rectangle}),
    getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerY),
    getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerY),
    getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2), losPlayerY),
    getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2), losPlayerY)
  ];

  const underCenterQb = getFormationPlayer(centerX, losPlayerY + shapeWidth + losPlayerSpacing, {alignment: PlayerGraphicAlignment.fill, color: '#008000'});
  const shotgunQb = getFormationPlayer(centerX, losY + (verticalYardUnit * 5), {alignment: PlayerGraphicAlignment.fill, color: '#008000'});
  const shortShotgunQb = getFormationPlayer(centerX, losY + (verticalYardUnit * 3.5), {alignment: PlayerGraphicAlignment.fill, color: '#008000'});
  
  const formationBackfields: FormationData[] = [
    {
       backfield: "EMPTY",
       formation: "",
       numBackfield: 1,
       players: [
          ...losPlayers,
          underCenterQb
       ]
    },
    {
       backfield: "ACE",
       formation: "",
       numBackfield: 2,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*6), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "LEFT",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "RIGHT",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "SPLIT",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX - (shapeWidth*2), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + (shapeWidth*2), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "SINGLE FB",
       formation: "",
       numBackfield: 2,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*4.5), {alignment: PlayerGraphicAlignment.fill}),
       ]
    },
    {
       backfield: "I-BACK",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*4.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "I-LEFT",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*7), {alignment: PlayerGraphicAlignment.fill}),
       ]
    },
    {
       backfield: "I-RIGHT",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*7), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL",
       formation: "",
       numBackfield: 2,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL WEAK",
       formation: "",
       numBackfield: 2,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL STRONG",
       formation: "",
       numBackfield: 2,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL SPLIT",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL FULLHOUSE",
       formation: "",
       numBackfield: 4,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*7.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL L",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*7.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL R",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*7.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN EMPTY",
       formation: "",
       numBackfield: 1,
       players: [
          ...losPlayers,
          shotgunQb
       ]
    },
    {
       backfield: "GUN L",
       formation: "",
       numBackfield: 2,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN R",
       formation: "",
       numBackfield: 2,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN SPLIT",
       formation: "",
       numBackfield: 3,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "WISHBONE",
       formation: "",
       numBackfield: 4,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*7), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*7), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "TEE",
       formation: "",
       numBackfield: 4,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "POWER I LEFT",
       formation: "",
       numBackfield: 4,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*7), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "POWER I RIGHT",
       formation: "",
       numBackfield: 4,
       players: [
          ...losPlayers,
          underCenterQb,
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX, losY + (verticalYardUnit*7), {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL SNIFFER L",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferLeftX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL SNIFFER R",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(centerX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferRightX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL WEAK SNIFFER L",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(snifferLeftX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferLeftX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL WEAK SNIFFER R",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(snifferLeftX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferRightX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL STRONG SNIFFER L",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(snifferRightX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferLeftX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "PISTOL STRONG SNIFFER R",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shortShotgunQb,
          getFormationPlayer(snifferRightX, losY + (verticalYardUnit*6.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferRightX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN EMPTY SNIFFER L",
       formation: "",
       numBackfield: 2,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(snifferLeftX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN EMPTY SNIFFER R",
       formation: "",
       numBackfield: 2,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(snifferRightX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN L SNIFFER L",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferLeftX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN L SNIFFER R",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferRightX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN R SNIFFER L",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferLeftX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN R SNIFFER R",
       formation: "",
       numBackfield: 3,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferRightX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN SPLIT SNIFFER L",
       formation: "",
       numBackfield: 4,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferLeftX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
    {
       backfield: "GUN SPLIT SNIFFER R",
       formation: "",
       numBackfield: 4,
       sniffer: true,
       players: [
          ...losPlayers,
          shotgunQb,
          getFormationPlayer(centerX - ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(centerX + ((shapeWidth*2) + (losPlayerSpacing*2)), losY + (verticalYardUnit*5.5), {alignment: PlayerGraphicAlignment.fill}),
          getFormationPlayer(snifferRightX, snifferY, {alignment: PlayerGraphicAlignment.fill})
       ]
    },
  ]
  
  const formationSides: FormationData[] = [
    {
      backfield: "",
      formation: "ZERO",
      numBackfield: 0,
      players: []
    },
    {
      backfield: "",
      formation: "SINGLE",
      numBackfield: 0,
      players: [
        getFormationPlayer(wideX, losPlayerY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TWINS",
      numBackfield: 1,
      players: [
        getFormationPlayer(wideX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(slotX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TRIPS",
      numBackfield: 1,
      players: [
        getFormationPlayer(wideX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(slotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightSlotX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      formation: "PRO",
      numBackfield: 1,
      players: [
        getFormationPlayer(tightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(wideX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      formation: "WING",
      numBackfield: 1,
      players: [
        getFormationPlayer(tightX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      formation: "TIGHT",
      numBackfield: 0,
      players: [
        getFormationPlayer(tightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      formation: "DOUBLE TIGHT",
      numBackfield: 0,
      players: [
        getFormationPlayer(doubleTightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      formation: "SLOT",
      numBackfield: 1,
      players: [
        getFormationPlayer(tightSlotX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TRIPS TIGHT",
      numBackfield: 1,
      players: [
        getFormationPlayer(wideX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(slotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TRIPS TIGHT WING",
      numBackfield: 1,
      players: [
        getFormationPlayer(wideX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(doubleTightX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TWINS WING",
      numBackfield: 1,
      players: [
        getFormationPlayer(wideX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TIGHT WING",
      numBackfield: 1,
      players: [
        getFormationPlayer(doubleTightX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TRIPS WING",
      numBackfield: 1,
      players: [
        getFormationPlayer(wideX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(slotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "QUADS",
      numBackfield: 2,
      players: [
        getFormationPlayer(wideX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(slotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightSlotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(doubleTightX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "QUADS WING",
      numBackfield: 2,
      players: [
        getFormationPlayer(wideX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(slotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightSlotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TRIPS BUNCH",
      numBackfield: 1,
      players: [
        getFormationPlayer(tightSlotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(doubleTightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(tightX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TWINS BUNCH",
      numBackfield: 1,
      players: [
        getFormationPlayer(tightSlotX, backY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(doubleTightX, losPlayerY, {alignment: PlayerGraphicAlignment.fill})
      ]
    },
    {
      backfield: "",
      formation: "TWINS STACK",
      numBackfield: 1,
      players: [
        getFormationPlayer(slotX, losPlayerY, {alignment: PlayerGraphicAlignment.fill}),
        getFormationPlayer(slotX, backY, {alignment: PlayerGraphicAlignment.fill})
      ]
    }
  ];

  return {losPlayers, formationBackfields, formationSides};
}

export const getGenericDefenseFormations = (size: PlaybookFieldSize = PlaybookFieldSize.short): FormationData[] => {
  const formAppConfg = playbookAppConfigMap.get(size);
  const yardHeight = size === PlaybookFieldSize.long ? formAppConfg.appHeight/70 : formAppConfg.appHeight/28;
  const losY = (yardHeight * (size === PlaybookFieldSize.long ? 40 : 15));
  const centerX = formAppConfg.appWidth/2;
  const shapeWidth = formAppConfg.defaultRadius*2;
  
  const losPlayerY = (losY + formAppConfg.defaultRadius + formAppConfg.losOffset);
  const losPlayerSpacing = size === PlaybookFieldSize.long ? 6 : 8;
  const losPlayerYDefense = (losY - formAppConfg.defaultRadius - formAppConfg.losOffset);

  const corners: PlaybookPlayer[] = [
    getFormationPlayer(shapeWidth * 5, losPlayerYDefense - yardHeight, {type: PlayerGraphicType.text, tag: 'C'}),
    getFormationPlayer(formAppConfg.appWidth - (shapeWidth * 5), losPlayerYDefense - yardHeight, {type: PlayerGraphicType.text, tag: 'C'}),
  ]
  const cornersSymbols: PlaybookPlayer[] = [
    getFormationPlayer(shapeWidth * 5, losPlayerYDefense - yardHeight, {type: PlayerGraphicType.triangle}),
    getFormationPlayer(formAppConfg.appWidth - (shapeWidth * 5), losPlayerYDefense - yardHeight, {type: PlayerGraphicType.triangle}),
  ]
  const splitSafeties: PlaybookPlayer[] = [
    getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 8), losPlayerYDefense - (yardHeight * 10), {type: PlayerGraphicType.text, tag: 'FS'}),
    getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 8), losPlayerYDefense - (yardHeight * 10), {type: PlayerGraphicType.text, tag: 'SS'})
  ];
  const splitSafetiesSymbols: PlaybookPlayer[] = [
    getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 8), losPlayerYDefense - (yardHeight * 10), {type: PlayerGraphicType.triangle}),
    getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 8), losPlayerYDefense - (yardHeight * 10), {type: PlayerGraphicType.triangle})
  ];
  const singleSafety: PlaybookPlayer = getFormationPlayer(centerX, losPlayerYDefense - (yardHeight * 10), {type: PlayerGraphicType.text, tag: 'FS'});
  const singleSafetySymbol: PlaybookPlayer = getFormationPlayer(centerX, losPlayerYDefense - (yardHeight * 10), {type: PlayerGraphicType.triangle});
  
  return [
    {
       formation: "3-4",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX, losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'N'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'W'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'B'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'M'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'S'}),
        ...corners,
        ...splitSafeties
      ]
    },
    {
       formation: "3-4 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX, losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        ...splitSafetiesSymbols
      ]
    },
    {
       formation: "4-1",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'D'}),
        getFormationPlayer(centerX, losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'M'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'N'}),
        ...corners,
        ...splitSafeties
      ]
    },
    {
       formation: "4-1 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX, losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        ...splitSafetiesSymbols
      ]
    },
    {
       formation: "4-2",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'W'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'M'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'N'}),
        ...corners,
        ...splitSafeties
      ]
    },
    {
       formation: "4-2 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        ...splitSafetiesSymbols
      ]
    },
    {
       formation: "4-3",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'W'}),
        getFormationPlayer(centerX, losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'M'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'S'}),
        ...corners,
        ...splitSafeties
      ]
    },
    {
       formation: "4-3 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX, losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        ...splitSafetiesSymbols
      ]
    },
    {
       formation: "4-4",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'W'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'B'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'M'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'S'}),
        ...corners,
        singleSafety
      ]
    },
    {
       formation: "4-4 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        singleSafetySymbol
      ]
    },
    {
       formation: "5-2",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX, losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'N'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 4), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'W'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 4), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'S'}),
        ...corners,
        ...splitSafeties
      ]
    },
    {
       formation: "5-2 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX, losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 4), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 4), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        ...splitSafetiesSymbols
      ]
    },
    {
       formation: "5-3",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX, losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'N'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'W'}),
        getFormationPlayer(centerX, losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'M'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'S'}),
        ...corners,
        singleSafety
      ]
    },
    {
       formation: "5-3 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX, losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX, losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        singleSafetySymbol
      ]
    },
    {
       formation: "6-2",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 0.75), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 0.75), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'T'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'E'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'W'}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'B'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.text, tag: 'M'}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3.5), losPlayerYDefense, {type: PlayerGraphicType.text, tag: 'S'}),
        ...corners,
        singleSafety
      ]
    },
    {
       formation: "6-2 Symbols",
       numBackfield: 0,
       players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 0.75), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 0.75), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        ...cornersSymbols,
        singleSafetySymbol
      ]
    }
  ];
}

export const getGenericSpecialTeamsFormations = (size: PlaybookFieldSize = PlaybookFieldSize.short): FormationData[] => {
  const formAppConfg = playbookAppConfigMap.get(size);
  const yardHeight = size === PlaybookFieldSize.long ? formAppConfg.appHeight/70 : formAppConfg.appHeight/28;
  const verticalYardUnit = size === PlaybookFieldSize.long ? (yardHeight*1.5) : yardHeight;
  const losY = (yardHeight * (size === PlaybookFieldSize.long ? 50 : 20));
  const centerX = formAppConfg.appWidth/2;
  const shapeWidth = formAppConfg.defaultRadius*2;
  
  const losPlayerY = (losY + formAppConfg.defaultRadius + formAppConfg.losOffset);
  const losPlayerSpacing = size === PlaybookFieldSize.long ? 6 : 8;
  const losPlayerYDefense = (losY - formAppConfg.defaultRadius - formAppConfg.losOffset);
  const kickoffPlayerSpacing = ((formAppConfg.appWidth/2) - (shapeWidth*5.5)) / 6;
  const kickoffReturnSpacing = formAppConfg.appWidth/12;
  
  const losPlayers: PlaybookPlayer[] = [
    getFormationPlayer(centerX, losPlayerY, {type: PlayerGraphicType.rectangle}),
    getFormationPlayer(centerX + (shapeWidth + losPlayerSpacing), losPlayerY),
    getFormationPlayer(centerX - (shapeWidth + losPlayerSpacing), losPlayerY),
    getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2), losPlayerY),
    getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2), losPlayerY)
  ];
  
  return [
    {
       formation: "Field Goal",
       numBackfield: 0,
       players: [
        ...losPlayers,
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerY + (shapeWidth/2)),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerY + (shapeWidth/2)),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 4), losPlayerY + shapeWidth),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 4), losPlayerY + shapeWidth),
        getFormationPlayer(centerX, losY + (verticalYardUnit * 5)),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2), losY + (verticalYardUnit * 7.5))
      ]
    },
    {
      formation: "Field Goal Block",
      numBackfield: 0,
      players: [
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 0.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 0.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 2.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 4.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 4.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX, losPlayerYDefense - (verticalYardUnit * 5), {type: PlayerGraphicType.triangle})
     ]
   },
   {
      formation: "Kickoff",
      numBackfield: 0,
      players: [
        getFormationPlayer(centerX, losPlayerY, {type: PlayerGraphicType.rectangle}),
        getFormationPlayer(centerX + ((shapeWidth + kickoffPlayerSpacing) * 1), losPlayerY),
        getFormationPlayer(centerX - ((shapeWidth + kickoffPlayerSpacing) * 1), losPlayerY),
        getFormationPlayer(centerX + ((shapeWidth + kickoffPlayerSpacing) * 2), losPlayerY),
        getFormationPlayer(centerX - ((shapeWidth + kickoffPlayerSpacing) * 2), losPlayerY),
        getFormationPlayer(centerX + ((shapeWidth + kickoffPlayerSpacing) * 3), losPlayerY),
        getFormationPlayer(centerX - ((shapeWidth + kickoffPlayerSpacing) * 3), losPlayerY),
        getFormationPlayer(centerX + ((shapeWidth + kickoffPlayerSpacing) * 4), losPlayerY),
        getFormationPlayer(centerX - ((shapeWidth + kickoffPlayerSpacing) * 4), losPlayerY),
        getFormationPlayer(centerX + ((shapeWidth + kickoffPlayerSpacing) * 5), losPlayerY),
        getFormationPlayer(centerX - ((shapeWidth + kickoffPlayerSpacing) * 5), losPlayerY),
     ]
   },
   {
      formation: "Kickoff Return",
      numBackfield: 0,
      players: [
        getFormationPlayer(centerX, (losPlayerYDefense - (verticalYardUnit * 3)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (kickoffReturnSpacing * 2), (losPlayerYDefense - (verticalYardUnit * 3)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (kickoffReturnSpacing * 2), (losPlayerYDefense - (verticalYardUnit * 3)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (kickoffReturnSpacing * 4), (losPlayerYDefense - (verticalYardUnit * 3)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (kickoffReturnSpacing * 4), (losPlayerYDefense - (verticalYardUnit * 3)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (kickoffReturnSpacing * 3), (losPlayerYDefense - (verticalYardUnit * 6)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (kickoffReturnSpacing * 3), (losPlayerYDefense - (verticalYardUnit * 6)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (kickoffReturnSpacing * 1), (losPlayerYDefense - (verticalYardUnit * 8)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (kickoffReturnSpacing * 1), (losPlayerYDefense - (verticalYardUnit * 8)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX + (kickoffReturnSpacing * 2), (losPlayerYDefense - (verticalYardUnit * 15)), {type: PlayerGraphicType.triangle}),
        getFormationPlayer(centerX - (kickoffReturnSpacing * 2), (losPlayerYDefense - (verticalYardUnit * 15)), {type: PlayerGraphicType.triangle}),
     ]
   },
   {
      formation: "Punt",
      numBackfield: 0,
      players: [
       ...losPlayers,
       getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerY + (shapeWidth/2)),
       getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerY + (shapeWidth/2)),
       getFormationPlayer(shapeWidth * 5, losPlayerY),
       getFormationPlayer(formAppConfg.appWidth - (shapeWidth * 5), losPlayerY),
       getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losY + (verticalYardUnit * 4)),
       getFormationPlayer(centerX, losY + (verticalYardUnit * 7))
     ]
   },
   {
     formation: "Punt Return",
     numBackfield: 0,
     players: [
      getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
      getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
      getFormationPlayer(centerX, losPlayerYDefense, {type: PlayerGraphicType.triangle}),
      getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 1.5), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
      getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 3), losPlayerYDefense, {type: PlayerGraphicType.triangle}),
      getFormationPlayer(centerX + ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
      getFormationPlayer(centerX - ((shapeWidth + losPlayerSpacing) * 6), losPlayerYDefense - (yardHeight*5), {type: PlayerGraphicType.triangle}),
      getFormationPlayer(shapeWidth * 5, losPlayerYDefense - yardHeight, {type: PlayerGraphicType.triangle}),
      getFormationPlayer(formAppConfg.appWidth - (shapeWidth * 5), losPlayerYDefense - yardHeight, {type: PlayerGraphicType.triangle}),
      getFormationPlayer(centerX, (losPlayerYDefense - (verticalYardUnit * 15)), {type: PlayerGraphicType.triangle})
    ]
  }
  ];
}


export const getGenericFormationPlays = (formations: FormationData[], type: PlaybookPlayType, fieldOptions: PlaybookFieldOptions = _.cloneDeep(defaultFieldOptions), flip: boolean = false) => {
  const size: PlaybookFieldSize = fieldOptions?.fieldSize ?? PlaybookFieldSize.short;
  const formAppConfg = playbookAppConfigMap.get(size);
  const yardHeight = size === PlaybookFieldSize.long ? formAppConfg.appHeight/70 : formAppConfg.appHeight/28;
  const losY = (yardHeight * (size === PlaybookFieldSize.long ? (type === PlaybookPlayType.specialteams ? 50 : 40) : (type === PlaybookPlayType.specialteams ? 20 : 15)));
  const plays: PlaybookPlay[] = [];
  _.forEach(formations, formation => {
    const play = getPlayFromFormationData(formation);
    play.type = type;
    play.name = play.formation = formation.formation;
    play.fieldOptions = fieldOptions;
    play.losY = losY;
    if (flip) {
      play.flipped = true;
      _.forEach(play.drawing ?? [], drawing => {
        const pos = drawing?.playerGraphic?.position;
        if (pos) {
          pos.y = formAppConfg.appHeight - pos.y;
          drawing.playerGraphic.position = pos;
        }
      });
      play.losY = (formAppConfg.appHeight - losY);
    }
    plays.push(play);
  });
  return plays;
}

export function getAllFormationPlays(): PlaybookPlay[] {
  let formationPlays: PlaybookPlay[] = [];
  const appConfig = playbookAppConfigMap.get(PlaybookFieldSize.short);
  const yardHeight = appConfig.appHeight/28;

  const {formationBackfields, formationSides} = getFormationBackfieldAndSides();
  _.forEach(formationBackfields, backfieldData => {
    _.forEach(formationSides, leftSideData => {
      _.forEach(formationSides, rightSideData => {
        if ((backfieldData.players.length + leftSideData.players.length + rightSideData.players.length) === 11 && (backfieldData.numBackfield + leftSideData.numBackfield + rightSideData.numBackfield) < 5) {
          rightSideData = _.cloneDeep(rightSideData);
          _.forEach(rightSideData.players, player => {
            player.position.x = appConfig.appWidth - player.position.x;
          });
          let play = new PlaybookPlay();
          play.formation = play.name = `${leftSideData.formation}-${rightSideData.formation} ${backfieldData.backfield}`;
          play.backfield = backfieldData.backfield;
          play.hash = 'M';
          play.losY = (yardHeight * 15);
          play.drawing = [];
          _.forEach([...backfieldData.players, ...leftSideData.players, ...rightSideData.players], playerGraphic => {
            play.drawing.push({
              playerGraphic,
              routes: []
            });
          });
          formationPlays.push(play);
        }
      });
    });
  });
  return formationPlays;
}
